import React from "react";
import { Link } from "react-router-dom";
import {
  OperatorProject,
  OperatorProjectCall,
  OperatorProjectArticle,
} from "../../../services/grantsApi";
import {
  CALL_STATUS_LABELS,
  CALL_STATUS_COLORS,
} from "../../../constants/grants-labels";
import { isSafeUrl } from "../../../utils/url-validation";

/**
 * Maximum article title length before truncation (frontend-side).
 */
const MAX_ARTICLE_TITLE_LENGTH = 50;

/**
 * Label displayed before action code in project row (Polish: "action" in the context of operational program).
 */
const ACTION_CODE_LABEL = "Działanie";

/**
 * Truncates text to maxLength and appends "..." if truncated.
 */
export const truncateTitle = (title: string, maxLength: number): string => {
  if (title.length <= maxLength) return title;
  return title.slice(0, maxLength) + "...";
};

/**
 * Single article link (truncated title, opens in new tab)
 */
export const ArticleLink: React.FC<{ article: OperatorProjectArticle }> = ({ article }) => {
  const displayTitle = truncateTitle(article.title, MAX_ARTICLE_TITLE_LENGTH);
  const isTruncated = article.title.length > MAX_ARTICLE_TITLE_LENGTH;

  if (!isSafeUrl(article.url)) {
    return <span className="text-xs text-neutral-500">{displayTitle}</span>;
  }

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-primary-600 hover:text-primary-800 hover:underline focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
      title={isTruncated ? article.title : undefined}
    >
      {displayTitle}
    </a>
  );
};

/**
 * Single call row with status badge and articles
 */
export const CallRow: React.FC<{ call: OperatorProjectCall }> = ({ call }) => {
  const statusLabel = CALL_STATUS_LABELS[call.status] || call.status;
  const statusColor = CALL_STATUS_COLORS[call.status] || "bg-neutral-200 text-neutral-800";

  return (
    <div className="flex items-start gap-2">
      <Link
        to={`/grants/calls/${call.id}`}
        className="text-xs text-primary-600 hover:text-primary-800 hover:underline whitespace-nowrap focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
      >
        {call.callNumber || call.title}
      </Link>
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${statusColor}`}>
        {statusLabel}
      </span>
      {call.articles.length > 0 && (
        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
          {call.articles.map((article) => (
            <ArticleLink key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Single project row: project name + list of calls
 */
export const ProjectRow: React.FC<{ project: OperatorProject }> = ({ project }) => (
  <div className="py-2 border-b border-neutral-100 last:border-b-0">
    <div className="flex items-start gap-4">
      <div className="w-1/3 min-w-0">
        <Link
          to={`/grants/projects/${project.id}`}
          className="text-sm text-primary-600 hover:text-primary-800 hover:underline font-medium truncate block focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
        >
          {project.name}
        </Link>
        {project.actionCode && (
          <div
            className="text-xs text-neutral-500 mt-0.5 truncate"
            title={`${ACTION_CODE_LABEL} ${project.actionCode}`}
          >
            {ACTION_CODE_LABEL} {project.actionCode}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {project.calls.length === 0 ? (
          <span className="text-xs text-neutral-400 italic">Brak naborów</span>
        ) : (
          <div className="space-y-1">
            {project.calls.map((call) => (
              <CallRow key={call.id} call={call} />
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

/**
 * Props for OperatorProjectTree component
 */
interface OperatorProjectTreeProps {
  projects: OperatorProject[];
}

/**
 * Renders a tree of projects with nested calls and articles.
 * Reusable presentation component extracted from OperatorOtherProjectsSection.
 *
 * @see docs/specifications/2026-06-05-shared-source-operators-expand-projects/spec.md
 */
const OperatorProjectTree: React.FC<OperatorProjectTreeProps> = ({ projects }) => (
  <div className="divide-y divide-neutral-100">
    {projects.map((project) => (
      <ProjectRow key={project.id} project={project} />
    ))}
  </div>
);

export default OperatorProjectTree;
